import {
  type CheckoutLineItem,
  type CheckoutEventsData,
  type PaddleEventData,
  type CheckoutCustomer,
  type PricePreviewResponse,
} from "@paddle/paddle-js";

import { type TPriceId } from "@musicfy/contants/Subscriptions";
import {
  type PaddleTransaction,
  type PaddleSubscriptionUpdatePreview,
} from "@musicfy/types/paddle-server";
import { type InferQuery } from "@musicfy/utils";
import { type StripeChangePreview } from "@musicfy/utils/stripePlanChange";

declare module "@paddle/paddle-js" {
  /** Override values from the @paddle/paddle-js library */
  enum CheckoutEventNames {
    SUBSCRIPTION_FETCHED = "subscription.created",
  }

  interface BaseUpdateCheckoutParams {
    items: CheckoutLineItem[];
    customer: CheckoutCustomer;
  }

  interface CodeUpdateCheckoutParams extends BaseUpdateCheckoutParams {
    discountCode: string | null;
    discountId?: never;
  }

  interface DiscountUpdateCheckoutParams extends BaseUpdateCheckoutParams {
    discountCode?: never;
    discountId: string | null;
  }

  type UpdateCheckoutParams =
    | CodeUpdateCheckoutParams
    | DiscountUpdateCheckoutParams;

  interface ExtendedPaddle extends Paddle {
    Checkout: Paddle["Checkout"] & {
      updateCheckout: (checkout: UpdateCheckoutParams) => void;
    };
    Environment: Paddle["Environment"] & {
      detect: () => Environments | undefined;
      get: () => Environments;
    };
    active?: boolean;
  }

  function initializePaddle(
    options?: InitializePaddleOptions
  ): Promise<ExtendedPaddle | undefined>;
}

/** Custom types for working with our Paddle provider */

export enum PaddleScreens {
  CHECKOUT = "checkout",
  UPDATE_PAYMENT = "update-payment",
  PRICING = "pricing",
  UPGRADE = "upgrade",
  STRIPE_UPGRADE = "stripe-upgrade",
  STRIPE_CHECKOUT = "stripe-checkout",
}
export type PaddleScreen = `${PaddleScreens}`;
export type PaddleEventCallback = (event: PaddleEventData) => void;

/** Checkout */
type CheckoutEventCallback = (event: CheckoutEventsData) => void;
export type CheckoutEvents = {
  onLoad?: CheckoutEventCallback;
  onClose?: (data: undefined) => void;
  onCheckoutCompleted?: CheckoutEventCallback;
  onCheckoutUpdated?: CheckoutEventCallback;
  onPaymentInitiated?: CheckoutEventCallback;
  onError?: (error: PaddleEventData) => void;
  onSubscriptionCreated?: CheckoutEventCallback;
};
export interface PaddleCheckoutContext {
  checkoutData: CheckoutEventsData | undefined;
  setCheckoutData: (data: CheckoutEventsData | undefined) => void;
  checkoutError: PaddleEventData | undefined;
  setCheckoutError: (data: PaddleEventData | undefined) => void;
  isFetchingSubscriptionRef: React.MutableRefObject<boolean>;
  isCheckoutUpdating: boolean;
  setIsCheckoutUpdating: (isUpdating: boolean) => void;
}

/** Update Payment */
export type UpdatePaymentEventData = CheckoutEventsData;
type UpdatePaymentEventCallback = CheckoutEventCallback;
export type UpdatePaymentEvents = {
  onLoad?: UpdatePaymentEventCallback;
  onClose?: (data: undefined) => void;
  onError?: (error: PaddleEventData) => void;
  onPaymentUpdateUpdated?: UpdatePaymentEventCallback;
  onPaymentUpdateCompleted?: UpdatePaymentEventCallback;
  onPaymentUpdateInitiated?: UpdatePaymentEventCallback;
};
export interface PaddleUpdatePaymentContext {
  updatePaymentData: UpdatePaymentEventData | undefined;
  setUpdatePaymentData: (data: UpdatePaymentEventData | undefined) => void;
  updatePaymentError: PaddleEventData | undefined;
  setUpdatePaymentError: (data: PaddleEventData | undefined) => void;
}

/** Upgrade Preview */
export interface PaddleUpgradePreviewContext {
  upgradePreviewData: PaddleSubscriptionUpdatePreview | undefined;
  setUpgradePreviewData: (
    data: PaddleSubscriptionUpdatePreview | undefined
  ) => void;
  isUpgradeUpdating: boolean;
  setIsUpgradeUpdating: (isUpdating: boolean) => void;
  stripeChangePreview: StripeChangePreview | undefined;
  setStripeChangePreview: (data: StripeChangePreview | undefined) => void;
}

/** Prices */

export type PricePreviewData = PricePreviewResponse["data"];

export interface PaddlePricesContext {
  pricesData: PricePreviewData | undefined;
}

/** Transactions */
export interface PaddleTransactionsContext {
  transactions: PaddleTransaction[] | null;
  isLoading: boolean;
  getCustomerTransactions: InferQuery<
    "subscription",
    "getCustomerTransactions",
    "useMutation"
  >;
}

/** Stripe Embedded Checkout overlay (Paddle-style shell, Stripe iframe). */
export type StripeCheckoutState = {
  priceId: TPriceId;
  clientSecret: string | null;
  sessionId: string | null;
  subscriptionId: string | null;
  promoCode?: string;
  promoApplied: boolean;
  amountTotal: number | null;
  amountDiscount: number;
  currency: string;
  /** Account email from create session — used to prefill step 1. */
  email: string;
};

export interface StripeCheckoutContext {
  stripeCheckout: StripeCheckoutState | null;
  setStripeCheckout: (data: StripeCheckoutState | null) => void;
  isStripeCheckoutUpdating: boolean;
  setIsStripeCheckoutUpdating: (isUpdating: boolean) => void;
  isStripePaymentProcessing: boolean;
  setIsStripePaymentProcessing: (isProcessing: boolean) => void;
  canCloseStripeCheckout: boolean;
  setCanCloseStripeCheckout: (canClose: boolean) => void;
  isFetchingStripeSubscriptionRef: React.MutableRefObject<boolean>;
}

export interface PaddleContext {
  checkoutContext: PaddleCheckoutContext;
  pricesContext: PaddlePricesContext;
  updatePaymentContext: PaddleUpdatePaymentContext;
  upgradePreviewContext: PaddleUpgradePreviewContext;
  transactionsContext: PaddleTransactionsContext;
  stripeCheckoutContext: StripeCheckoutContext;
  subscribeToPaddleEvents: (eventCallback: PaddleEventCallback) => void;
  unsubscribeFromPaddleEvents: (eventCallback: PaddleEventCallback) => void;
  setActivePaddleScreen: (screen: PaddleScreen | null) => void;
  startCheckoutFlow: (origin?: string) => void;
  /**
   * Show the Pro-Feature upsell interstitial for a wall origin, then proceed to
   * checkout on the CTA. Call this instead of startCheckoutFlow at the pro
   * feature walls (stem-splitter / master-quality / premium-advanced-settings).
   */
  openProUpsell: (origin: string) => void;
  origin: string | undefined;
  setOrigin: (origin: string | undefined) => void;
}
